import { useSEO } from "../lib/useSEO";
import PageHero from "../components/layout/PageHero";
import SectionHeader from "../components/SectionHeader";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import { ContactForm } from "../components/Forms";
import { Reveal, Dots } from "../components/motion/Reveal";
import { brand, contactValue } from "../config/brand";

const CONTACT_ROWS = [
  { icon: "Phone", label: "Phone", value: brand.phone, href: brand.phone ? `tel:${brand.phone.replace(/\s/g, "")}` : null },
  { icon: "Mail", label: "Email", value: brand.email, href: brand.email ? `mailto:${brand.email}` : null },
  { icon: "MapPin", label: "Office Address", value: brand.address, href: null },
  { icon: "MessagesSquare", label: "WhatsApp", value: brand.whatsapp, href: brand.whatsappLink || null },
  { icon: "Clock", label: "Working Hours", value: brand.workingHours, href: null },
];

export default function Contact() {
  useSEO(
    "Contact Us — Suvera Academy",
    "Plan your next step with Suvera Academy. Submit an enquiry and an education advisor will be in touch."
  );
  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="Let's Plan Your Next Step"
        description="Questions about destinations, courses, scholarships or visas? Send us an enquiry and an education advisor will get back to you."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
        testid="contact-hero"
      />

      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader chapter="Reach Us" eyebrow="Get in Touch" title="We Would Love to Hear From You" />
            <Reveal>
              <ul className="space-y-4" data-testid="contact-details-list">
                {CONTACT_ROWS.map((r) => (
                  <li key={r.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy/5 text-navy">
                      <Icon name={r.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-400">{r.label}</p>
                      {r.href && r.value ? (
                        <a href={r.href} className="text-sm font-medium text-navy-dark transition-colors duration-150 hover:text-dot-blue" data-testid={`contact-${r.label.toLowerCase().replace(/\s+/g, "-")}-link`}>
                          {r.value}
                        </a>
                      ) : (
                        <p className={`text-sm font-medium ${r.value ? "text-navy-dark" : "italic text-slate-400"}`}>{contactValue(r.value)}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-6 flex items-center gap-2 text-xs text-slate-400">
                <Dots size="h-1 w-1" /> Prefer to talk? Call or WhatsApp us — or send an enquiry and we will reach out.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_8px_32px_rgba(27,47,91,0.06)] sm:p-10">
                <h2 className="mb-6 font-display text-2xl font-semibold text-navy-dark">Submit an Enquiry</h2>
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTASection />
    </main>
  );
}
